import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Save, Smartphone, Clock } from 'lucide-react'
import { botService } from '../services/botService'
import BusinessHoursConfig from '../components/BusinessHoursConfig'
import toast from 'react-hot-toast'
import { logger } from '../utils/logger'

const BotEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [bot, setBot] = useState<any>(null)
  const [showBusinessHours, setShowBusinessHours] = useState(false)
  const [formData, setFormData] = useState({
    session_name: '',
    phone_number: '',
    settings: {
      autoReply: false
    }
  })

  useEffect(() => {
    if (id) {
      loadBot()
    }
  }, [id])

  const loadBot = async () => {
    try {
      setIsLoading(true)
      const result = await botService.getBot(Number(id))
      
      if (result.success && result.data) {
        const botData = result.data
        setBot(botData)
        const settings =
          typeof botData.settings === 'string'
            ? (() => {
                try {
                  return JSON.parse(botData.settings) as Record<string, unknown>
                } catch {
                  return {}
                }
              })()
            : botData.settings && typeof botData.settings === 'object'
              ? botData.settings
              : {}
        setFormData({
          session_name: botData.session_name || '',
          phone_number: botData.phone_number || '',
          settings: {
            autoReply: settings.autoReply === true
          }
        })
      } else {
        toast.error(result.message || 'Erro ao carregar bot')
        navigate('/bots')
      }
    } catch (error) {
      logger.error('Erro ao carregar bot:', error)
      toast.error('Erro ao carregar bot')
      navigate('/bots')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    const checked = (e.target as HTMLInputElement).checked

    if (name.startsWith('settings.')) {
      const [_, prop] = name.split('.')
      setFormData(prev => ({
        ...prev,
        settings: {
          ...prev.settings,
          [prop]: type === 'checkbox' ? checked : value
        }
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }))
    }
  }

  const handleBusinessHoursClose = () => {
    setShowBusinessHours(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.session_name.trim()) {
      toast.error('Nome da sessão é obrigatório')
      return
    }

    try {
      setIsLoading(true)
      const result = await botService.updateBot(Number(id), formData)
      
      if (result.success) {
        toast.success('Bot atualizado com sucesso!')
        navigate('/bots')
      } else {
        toast.error(result.message || 'Erro ao atualizar bot')
      }
    } catch (error) {
      logger.error('Erro ao atualizar bot:', error)
      toast.error('Erro ao atualizar bot')
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading && !bot) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center min-h-96">
          <div className="loading-spinner w-8 h-8"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center space-x-4 mb-8">
        <button
          onClick={() => navigate('/bots')}
          className="p-2 hover:bg-gray-200 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center space-x-3">
          <Smartphone className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Editar Bot</h1>
            <p className="text-gray-600 mt-2">Atualize as configurações do seu chatbot</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Informações Básicas */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center space-x-3 mb-6">
            <Smartphone className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">Informações Básicas</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="session_name" className="block text-sm font-medium text-gray-700 mb-2">
                Nome da Sessão *
              </label>
              <input
                type="text"
                id="session_name"
                name="session_name"
                value={formData.session_name}
                onChange={handleInputChange}
                className="input w-full"
                placeholder="Ex: Atendimento Principal"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Nome único para identificar este bot
              </p>
            </div>

            <div>
              <label htmlFor="phone_number" className="block text-sm font-medium text-gray-700 mb-2">
                Número de Telefone
              </label>
              <input
                type="tel"
                id="phone_number"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleInputChange}
                className="input w-full"
                placeholder="Ex: 5511999999999"
              />
              <p className="text-xs text-gray-500 mt-1">
                Número do WhatsApp (opcional)
              </p>
            </div>
          </div>
        </div>

        {/* Configurações do Bot */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Configurações do Bot</h2>
          
          <div className="space-y-6">
            {/* Resposta Automática */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <h3 className="font-medium text-gray-900">Resposta Automática</h3>
                <p className="text-sm text-gray-600">
                  Bot responde automaticamente às mensagens
                </p>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  name="settings.autoReply"
                  checked={formData.settings.autoReply}
                  onChange={handleInputChange}
                />
                <span className="slider"></span>
              </label>
            </div>

            {/* Botão para configurar horário comercial */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-gray-900">Horário Comercial</h3>
                  <p className="text-sm text-gray-600">
                    Configure o horário de funcionamento do bot
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowBusinessHours(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <Clock className="h-4 w-4" />
                  <span>Configurar</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Botões */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/bots')}
            className="btn btn-md btn-outline"
            disabled={isLoading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-md btn-primary"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="loading-spinner w-4 h-4 mr-2"></div>
                Salvando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Salvar Alterações
              </>
            )}
          </button>
        </div>
      </form>

      {/* Modal de Configuração de Horário Comercial */}
      {showBusinessHours && id && (
        <BusinessHoursConfig
          botId={Number(id)}
          onClose={handleBusinessHoursClose}
        />
      )}
      </div>
    </div>
  )
}

export default BotEdit
